// src/pages/RunAgentPage.jsx

import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useWallet } from "../contexts/WalletContext";
import {
  getAgentById,
  runAgent as apiRunAgent,
  getAgentRunStatus,
} from "../services/agentService";
import Button from "../components/common/Button";
import Spinner from "../components/common/Spinner";
import {
  ArrowLeftIcon,
  PlayCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  CpuChipIcon,
  PaintBrushIcon,
  ChatBubbleLeftEllipsisIcon,
  DocumentChartBarIcon,
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
  MusicalNoteIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

const IMAGE_AGENT_TYPE_ID_EXAMPLE = "2a9dde84-e3d4-464d-8e6d-4567f77d992d";
const TEXT_AGENT_TYPE_ID_EXAMPLE = "38ee2462-5c9b-4ce9-a4e2-8a2876c7883e";
const TEXT_AGENT_TYPE_ID_EXAMPLE2 = "66d4e15f-1951-43e7-afe9-99d571d707ca"; // Added second ID
const MUSIC_AGENT_TYPE_ID_EXAMPLE = "c1787718-02c5-4a04-90e0-0fdabb8729e4";
const VIDEO_AGENT_TYPE_ID_EXAMPLE = "4a5ba4e6-9d8a-4806-8d4f-230763768eb3"; // Example ID for video agent

const getAgentTypeVisuals = (agentTypeNameOrId, agentTypeIdFromAgentObject) => {
  const idForIcon =
    agentTypeIdFromAgentObject || agentTypeNameOrId?.toLowerCase() || "";
  const name = agentTypeNameOrId?.toLowerCase() || "";
  let icon = <CpuChipIcon className="h-6 w-6 text-slate-400" />;
  let color = "bg-slate-700 text-slate-300";
  let accentColor = "border-slate-500";

  if (
    idForIcon === IMAGE_AGENT_TYPE_ID_EXAMPLE ||
    name.includes("image") ||
    name.includes("dall-e")
  ) {
    icon = <PaintBrushIcon className="h-6 w-6 text-pink-400" />;
    color = "bg-pink-500/20 text-pink-300";
    accentColor = "border-pink-500";
  } else if (
    idForIcon === TEXT_AGENT_TYPE_ID_EXAMPLE ||
    idForIcon === TEXT_AGENT_TYPE_ID_EXAMPLE2 ||
    name.includes("text") ||
    name.includes("chat") ||
    name.includes("gpt")
  ) {
    icon = <ChatBubbleLeftEllipsisIcon className="h-6 w-6 text-sky-400" />;
    color = "bg-sky-500/20 text-sky-300";
    accentColor = "border-sky-500";
  } else if (
    idForIcon === MUSIC_AGENT_TYPE_ID_EXAMPLE ||
    name.includes("music") ||
    name.includes("audio")
  ) {
    icon = <MusicalNoteIcon className="h-6 w-6 text-purple-400" />;
    color = "bg-purple-500/20 text-purple-300";
    accentColor = "border-purple-500";
  } else if (
    idForIcon === VIDEO_AGENT_TYPE_ID_EXAMPLE ||
    name.includes("video") ||
    name.includes("film") ||
    name.includes("movie")
  ) {
    icon = <VideoCameraIcon className="h-6 w-6 text-orange-400" />;
    color = "bg-orange-500/20 text-orange-300";
    accentColor = "border-orange-500";
  } else if (
    name.includes("code") ||
    name.includes("script") ||
    name.includes("programming")
  ) {
    icon = <CpuChipIcon className="h-6 w-6 text-yellow-400" />;
    color = "bg-yellow-500/20 text-yellow-300";
    accentColor = "border-yellow-500";
  } else if (name.includes("chart") || name.includes("analyzer")) {
    icon = <DocumentChartBarIcon className="h-6 w-6 text-emerald-400" />;
    color = "bg-emerald-500/20 text-emerald-300";
    accentColor = "border-emerald-500";
  }
  return { icon, color, accentColor };
};

const RunAgentPage = () => {
  const { agentId } = useParams();
  const { currentAccount } = useWallet();

  const [agent, setAgent] = useState(null);
  const [userInput, setUserInput] = useState("");
  const [runResult, setRunResult] = useState(null);
  const [isLoadingAgent, setIsLoadingAgent] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [agentError, setAgentError] = useState("");
  const [runError, setRunError] = useState("");

  const pollingIntervalRef = React.useRef(null); // Ref to hold the interval ID
  const stopPolling = useCallback(() => {
    // Bungkus dengan useCallback
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      console.log("Polling stopped.");
    }
  }, []); // Dependensi kosong karena tidak bergantung pada state/props

  // Pastikan interval dibersihkan saat komponen unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]); // Tambahkan stopPolling ke dependensi untuk memastikan cleanup yang benar

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);
  const fetchAgentDetails = useCallback(async () => {
    if (!agentId) return;
    setIsLoadingAgent(true);
    setAgentError("");
    setRunResult(null);
    setUserInput("");
    try {
      const data = await getAgentById(agentId, currentAccount);
      setAgent(data);
    } catch (error) {
      console.error("Failed to fetch agent details:", error);
      setAgentError(error.message || "Could not load agent details.");
    } finally {
      setIsLoadingAgent(false);
    }
  }, [agentId, currentAccount]);

  useEffect(() => {
    fetchAgentDetails();
  }, [fetchAgentDetails]);

  useEffect(() => {
    // Jalankan fetch saat komponen pertama kali dimuat atau dependensi berubah
    fetchAgentDetails();

    // Fungsi cleanup ini akan berjalan saat komponen unmount ATAU
    // saat salah satu dependensi di array (fetchAgentDetails) berubah.
    // Ini memastikan polling dari proses sebelumnya selalu dibersihkan.
    return () => {
      stopPolling();
    };
  }, [fetchAgentDetails, stopPolling]); // Tambahkan stopPolling ke dependensi

  const pollForStatus = useCallback(
    (taskId) => {
      stopPolling(); // Hentikan polling sebelumnya jika ada

      pollingIntervalRef.current = setInterval(async () => {
        console.log(`Polling for task ID: ${taskId}`);
        try {
          const statusResponse = await getAgentRunStatus(
            taskId,
            currentAccount
          );

          if (statusResponse.status === "SUCCESS") {
            stopPolling();
            setIsRunning(false);
            // setRunResult(statusResponse.result); // Hasil sekarang ada di 'result'
            setRunResult({
              result: statusResponse.result,
              service: statusResponse.serviceType || "unknown",
              message: statusResponse.message || "",
              tracks: statusResponse.tracks || [],
            });
            console.log("Agent run completed successfully:", statusResponse);
            toast.success("Agent run completed successfully!");
          } else if (statusResponse.status === "ERROR") {
            stopPolling();
            setIsRunning(false);
            const errorMessage =
              statusResponse.error || "Agent run failed with an unknown error.";
            setRunError(errorMessage);
            toast.error(errorMessage);
          }
          // Jika status masih 'PROCESSING', interval akan terus berjalan
        } catch (error) {
          stopPolling();
          setIsRunning(false);
          const errorMessage = "Failed to get task status: " + error.message;
          setRunError(errorMessage);
          toast.error(errorMessage);
        }
      }, 5000); // Poll setiap 5 detik
    },
    [currentAccount, stopPolling]
  );

  const handleRunAgent = async (e) => {
    e.preventDefault();
    if (!currentAccount) {
      setRunError("Wallet not connected. Please connect your wallet.");
      toast.error("Wallet not connected.");
      return;
    }
    if (
      !userInput.trim() &&
      agent?.configuration?.serviceType !==
        "some_agent_type_that_needs_no_input"
    ) {
      setRunError("User input cannot be empty for this agent.");
      toast.error("User input cannot be empty.");
      return;
    }

    setIsRunning(true);
    setRunError("");
    setRunResult(null);
    toast.loading("Initiating agent run...", { id: "running-toast" });

    try {
      // 1. Panggil API untuk memulai tugas
      const initialResponse = await apiRunAgent(
        agentId,
        userInput,
        currentAccount
      );

      // Handle Direct Result (Synchronous Backend)
      if (initialResponse && initialResponse.result) {
        toast.dismiss("running-toast");
        toast.success("Agent run completed successfully!");
        setRunResult({
          result: initialResponse.result,
          service: initialResponse.serviceUsed || initialResponse.result.service || "unknown",
        });
        setIsRunning(false);
      } 
      // Handle Async Task (Polling Backend)
      else if (initialResponse && initialResponse.taskId) {
        toast.success("Agent task started! Checking for results...", {
          id: "running-toast",
        });
        pollForStatus(initialResponse.taskId);
      } else {
        throw new Error("Did not receive a valid result or task ID from the server.");
      }
    } catch (error) {
      // Menangkap error dari panggilan API awal (bukan dari proses AI-nya)
      toast.dismiss("running-toast");
      const errorMessage =
        error.message || "An error occurred while starting the agent.";
      toast.error(errorMessage);
      setRunError(errorMessage);
      setIsRunning(false); // Hentikan loading jika inisiasi gagal
    }
    // No 'finally' block to set isRunning(false) here, as it's now controlled by the polling result.
  };

  const copyToClipboard = (text, type = "Text") => {
    if (navigator.clipboard && text) {
      navigator.clipboard
        .writeText(text)
        .then(() => toast.success(`${type} copied to clipboard!`))
        .catch((err) => {
          console.error("Failed to copy text: ", err);
          toast.error(`Failed to copy ${type.toLowerCase()}.`);
        });
    } else if (text) {
      try {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success(`${type} copied to clipboard! (fallback)`);
      } catch (err) {
        console.error("Fallback copy failed: ", err);
        toast.error(`Failed to copy ${type.toLowerCase()}.`);
      }
    }
  };

  let inputPlaceholder = "Enter your text prompt or input here...";
  let inputFieldType = "textarea";
  let inputRows = 6;

  if (agent?.configuration?.serviceType) {
    const serviceType = agent.configuration.serviceType.toLowerCase();
    if (serviceType.includes("image_url") || serviceType.includes("url")) {
      inputPlaceholder =
        "Enter URL for analysis (e.g., https://example.com/image.png)";
      inputFieldType = "url";
      inputRows = 1;
    } else if (
      serviceType.includes("json_data") ||
      serviceType.includes("data_input")
    ) {
      inputPlaceholder = 'Enter JSON data here (e.g., {"key": "value"})';
      inputRows = 10;
    } else if (serviceType.includes("image_generation")) {
      inputPlaceholder = "Describe the image you want to generate in detail...";
    } else if (serviceType.includes("music") || serviceType.includes("audio") || serviceType.includes("suno")) {
      inputPlaceholder = "Describe the song or audio you want to generate (e.g., 'An upbeat pop song about AI assistants')";
    }
  }

  // 1) Loading state
  if (isLoadingAgent) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // 2) Fetch error
  if (agentError) {
    return (
      <div className="p-6 text-center text-red-400">
        <ExclamationTriangleIcon className="h-6 w-6 inline-block mr-2" />
        {agentError}
      </div>
    );
  }

  // 3) No agent found
  if (!agent) {
    return (
      <div className="p-6 text-center text-slate-500">Agent not found.</div>
    );
  }

  // … now agent is guaranteed non-null, safe to read agent.name below …
  const agentVisuals = getAgentTypeVisuals(
    agent.agent_type_name || agent.configuration?.serviceType,
    agent.agent_type_id
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header Bar */}
      <div className="flex items-center justify-between mb-8">
        <Link
          to="/my-agents"
          className="group flex items-center text-sm font-medium text-slate-400 hover:text-white transition-colors py-2 px-4 rounded-lg hover:bg-slate-800/50"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          Back to Fleet
        </Link>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 rounded-full border border-slate-700/50">
             <span className={`w-2 h-2 rounded-full ${isRunning ? 'bg-green-500 animate-pulse' : 'bg-slate-500'}`}></span>
             <span className="text-xs text-slate-300 font-mono uppercase tracking-wide">
                {isRunning ? 'System Active' : 'System Ready'}
             </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[calc(100vh-140px)] min-h-[600px]">
        
        {/* LEFT COLUMN: Agent Info & Configuration */}
        <div className="lg:col-span-4 flex flex-col gap-6">
            {/* Agent Identity Card */}
            <div className={`relative overflow-hidden bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-slate-800 shadow-2xl p-6 group`}>
                <div className={`absolute top-0 inset-x-0 h-1 bg-gradient-to-r ${agentVisuals.color.replace('text-', 'from-').replace('300', '500').replace('400', '500')} to-transparent opacity-75`}></div>
                
                <div className="flex items-start justify-between mb-6">
                    <div className={`p-4 rounded-2xl ${agentVisuals.color.replace('text-', 'bg-').replace('300', '500/10').replace('400', '500/10')} border border-white/5`}>
                         {agentVisuals.icon}
                    </div>
                </div>

                <div>
                    <h1 className="text-2xl font-bold text-white mb-2 leading-tight">
                        {agent.name}
                    </h1>
                    <div className="flex flex-wrap gap-2 mb-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-slate-800 text-slate-300 border border-slate-700/50">
                            {agent.agent_type_name || agent.configuration?.serviceType || "N/A"}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-slate-800 text-slate-500 border border-slate-700/50 font-mono">
                           v1.0.0
                        </span>
                    </div>
                    <p className="text-sm text-slate-400 leading-relaxed font-light">
                        {agent.description || "No description provided."}
                    </p>
                </div>

                <div className="mt-6 pt-6 border-t border-slate-800/50">
                    <div className="flex items-center justify-between text-xs text-slate-500 font-mono">
                        <span>ID</span>
                        <span className="truncate ml-4">{agent.agent_id}</span>
                    </div>
                </div>
            </div>

            {/* Input Config Panel */}
            <div className="flex-1 bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-slate-800 shadow-2xl p-6 flex flex-col">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                    Input Parameters
                </h3>
                
                <form id="runAgentForm" onSubmit={handleRunAgent} className="flex-1 flex flex-col">
                     <div className="flex-1 min-h-[120px] mb-4">
                        {inputFieldType === "textarea" ? (
                        <textarea
                            id="userInput"
                            name="userInput"
                            className="w-full h-full bg-slate-950/50 border border-slate-800 rounded-xl p-4 text-slate-200 placeholder-slate-600 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all resize-none font-mono text-sm leading-relaxed custom-scrollbar"
                            placeholder={inputPlaceholder}
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            disabled={isRunning}
                        />
                        ) : (
                        <input
                            id="userInput"
                            name="userInput"
                            type={inputFieldType}
                            className="w-full bg-slate-950/50 border border-slate-800 rounded-xl p-4 text-slate-200 placeholder-slate-600 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all font-mono text-sm"
                            placeholder={inputPlaceholder}
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            disabled={isRunning}
                        />
                        )}
                    </div>
                    
                    <Button
                        type="submit"
                        disabled={isRunning || !currentAccount}
                        className={`w-full py-4 rounded-xl font-semibold text-sm tracking-wide transition-all shadow-lg
                            ${isRunning 
                                ? 'bg-slate-800 text-slate-400 cursor-not-allowed border border-slate-700' 
                                : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-blue-900/20 border border-transparent'
                            }`}
                    >
                        <div className="flex items-center justify-center gap-2">
                            {isRunning ? (
                                <>
                                    <Spinner size="sm" color="gray" />
                                    <span>PROCESSING SEQUENCE...</span>
                                </>
                            ) : (
                                <>
                                    <PlayCircleIcon className="h-5 w-5" />
                                    <span>EXECUTE AGENT</span>
                                </>
                            )}
                        </div>
                    </Button>
                    {!currentAccount && (
                        <p className="mt-3 text-center text-xs text-red-400 bg-red-900/10 py-2 rounded-lg border border-red-900/20">
                            Wallet connection required
                        </p>
                    )}
                </form>
            </div>
        </div>

        {/* RIGHT COLUMN: Terminal Output */}
        <div className="lg:col-span-8 flex flex-col h-full min-h-[500px]">
             <div className="relative flex-1 bg-[#0D1117] rounded-2xl border border-slate-800 shadow-2xl flex flex-col overflow-hidden font-mono text-sm">
                
                {/* Terminal Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-slate-900/50 border-b border-slate-800">
                    <div className="flex items-center gap-2">
                        <div className="flex gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/50"></div>
                            <div className="w-3 h-3 rounded-full bg-yellow-500/20 border border-yellow-500/50"></div>
                            <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500/50"></div>
                        </div>
                        <span className="ml-3 text-slate-500 text-xs">agent_terminal_output</span>
                    </div>
                    <div className="flex items-center gap-3">
                        {runResult && (
                             <button 
                                onClick={() => copyToClipboard(JSON.stringify(runResult, null, 2), "Raw Data")}
                                className="text-xs text-slate-500 hover:text-blue-400 transition-colors flex items-center gap-1"
                            >
                                <ClipboardDocumentIcon className="w-3 h-3" />
                                Copy Raw
                            </button>
                        )}
                        <span className={`text-[10px] uppercase tracking-wider ${isRunning ? 'text-green-500 animate-pulse' : 'text-slate-600'}`}>
                            {isRunning ? '● LIVE' : '● OFFLINE'}
                        </span>
                    </div>
                </div>

                {/* Terminal Window */}
                <div className="flex-1 p-6 overflow-y-auto custom-scrollbar relative">
                    
                    {/* Welcome/Empty State */}
                    {!runResult && !isRunning && !runError && (
                        <div className="h-full flex flex-col items-center justify-center text-slate-600 opacity-50">
                             <div className="w-16 h-16 mb-4 rounded-xl border border-slate-700 bg-slate-800/50 flex items-center justify-center">
                                <span className="font-mono text-2xl font-bold">{'>_'}</span>
                             </div>
                             <p>Waiting for command sequence...</p>
                        </div>
                    )}

                    {/* Active State / Stream */}
                    <div className="space-y-4">
                        {/* Simulation CLI Logs */}
                        {isRunning && (
                             <div className="text-slate-400 space-y-1">
                                <div className="flex gap-2">
                                    <span className="text-blue-500">➜</span>
                                    <span>Initializing connection to agent node...</span>
                                </div>
                                <div className="flex gap-2 animate-pulse">
                                    <span className="text-blue-500">➜</span>
                                    <span>Processing input parameters...</span>
                                </div>
                            </div>
                        )}

                        {/* Actual Result Render */}
                         {runResult && !isRunning && (
                            <div className="animate-fadeIn">
                                 <div className="flex gap-2 mb-4 text-green-500/80 border-b border-green-900/30 pb-2">
                                    <span>✔</span>
                                    <span>Process completed successfully.</span>
                                </div>

                                {runResult.result && (
                                    <div className="pl-4 border-l-2 border-slate-800 space-y-4">
                                         
                                         {/* --- Image Renderer --- */}
                                         {typeof runResult.result === "object" && runResult.result.service === IMAGE_AGENT_TYPE_ID_EXAMPLE && (
                                            <div className="max-w-md">
                                                <img 
                                                    src={runResult.result.imageUrl} 
                                                    alt="Generated Output" 
                                                    className="rounded-lg border border-slate-700 shadow-2xl"
                                                />
                                            </div>
                                         )}

                                          {/* --- Text Renderer --- */}
                                          {typeof runResult.result === "object" && (runResult.result.service === TEXT_AGENT_TYPE_ID_EXAMPLE || runResult.result.service === TEXT_AGENT_TYPE_ID_EXAMPLE2) && (
                                              <div className="prose prose-invert prose-sm max-w-none text-slate-300">
                                                  {runResult.result.text}
                                              </div>
                                          )}

                                          {/* --- Audio Renderer --- */}
                                          {runResult.result && (typeof runResult.result === "object") && (runResult.result.type === "audio_collection" || runResult.service === MUSIC_AGENT_TYPE_ID_EXAMPLE) && runResult.result.result && runResult.result.result.tracks && (
                                              <div className="space-y-4 w-full">
                                                <p className="text-xs text-slate-500 uppercase tracking-widest font-bold mb-2">Generated Audio Tracks</p>
                                                  {Array.isArray(runResult.result.result.tracks) && runResult.result.result.tracks.map((track, index) => (
                                                      <div key={track.id || index} className="bg-slate-900/50 p-3 rounded-lg border border-slate-800 flex flex-col gap-3">
                                                          <div className="flex items-center gap-3">
                                                              <div className="w-10 h-10 rounded bg-slate-800 flex items-center justify-center flex-shrink-0 border border-slate-700">
                                                                  {track.imageUrl ? <img src={track.imageUrl} alt="art" className="w-full h-full object-cover rounded"/> : <MusicalNoteIcon className="w-5 h-5 text-purple-500"/>}
                                                              </div>
                                                              <div className="min-w-0 flex-1">
                                                                  <p className="text-sm font-bold text-slate-200 truncate font-mono">{track.title || "Untitled_Track"}</p>
                                                                  <p className="text-[10px] text-slate-500 font-mono">
                                                                      {track.duration ? `${Math.floor(track.duration / 60)}:${Math.round(track.duration % 60).toString().padStart(2, "0")}` : "00:00"}
                                                                  </p>
                                                              </div>
                                                              <a href={track.audioUrl} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-500 hover:text-green-400 transition-colors" title="Download">
                                                                  <ArrowDownTrayIcon className="w-4 h-4"/>
                                                              </a>
                                                          </div>
                                                          <audio controls className="w-full h-6 opacity-60 hover:opacity-100 transition-opacity" controlsList="nodownload">
                                                              <source src={track.audioUrl} type="audio/mpeg" />
                                                          </audio>
                                                          {track.lyric && (
                                                            <details className="text-[10px] text-slate-500">
                                                              <summary className="cursor-pointer hover:text-slate-300">View Lyrics</summary>
                                                              <pre className="mt-2 text-slate-400 whitespace-pre-wrap font-mono pl-2 border-l border-slate-800">{track.lyric}</pre>
                                                            </details>
                                                          )}
                                                      </div>
                                                  ))}
                                              </div>
                                          )}

                                          {/* --- Video Renderer --- */}
                                          {runResult.result && (typeof runResult.result === "object") && (runResult.result.type === "video" || runResult.service === VIDEO_AGENT_TYPE_ID_EXAMPLE) && runResult.result.result && runResult.result.result.videoUrl && (
                                              <div className="w-full">
                                                   <p className="text-xs text-slate-500 uppercase tracking-widest font-bold mb-2">Generated Video Output</p>
                                                   <div className="relative rounded-lg overflow-hidden border border-slate-800 bg-black shadow-lg">
                                                       <video controls className="w-full max-h-[400px]">
                                                           <source src={runResult.result.result.videoUrl} type="video/mp4" />
                                                       </video>
                                                   </div>
                                                   <div className="flex items-center justify-between mt-3 px-1">
                                                       <div className="text-[10px] text-slate-500 font-mono">
                                                          ID: {runResult.result.result.taskId} | {parseFloat(runResult.result.result.duration || 0).toFixed(1)}s
                                                       </div>
                                                       <div className="flex gap-3">
                                                           <a href={runResult.result.result.videoUrl} download target="_blank" className="text-xs flex items-center gap-1 text-slate-500 hover:text-green-400 transition-colors">
                                                               <ArrowDownTrayIcon className="w-3 h-3"/> Download
                                                           </a>
                                                            <button 
                                                                onClick={() => window.open(runResult.result.result.videoUrl, '_blank')}
                                                                className="text-xs flex items-center gap-1 text-slate-500 hover:text-blue-400 transition-colors"
                                                            >
                                                                <VideoCameraIcon className="w-3 h-3"/> maximize
                                                            </button>
                                                       </div>
                                                   </div>
                                              </div>
                                          )}

                                          {/* --- Default JSON Fallback --- */}
                                          {!(typeof runResult.result === "object" && 
                                              (runResult.result.service === IMAGE_AGENT_TYPE_ID_EXAMPLE || 
                                               runResult.result.service === TEXT_AGENT_TYPE_ID_EXAMPLE ||
                                               runResult.result.service === TEXT_AGENT_TYPE_ID_EXAMPLE2 ||
                                               runResult.result.service === MUSIC_AGENT_TYPE_ID_EXAMPLE ||
                                               runResult.result.type === "audio_collection")) && (
                                               <pre className="text-xs text-green-300/80 overflow-x-auto whitespace-pre-wrap font-mono bg-slate-950/50 p-4 rounded-lg border border-slate-800">
                                                    {typeof runResult.result === "string" 
                                                        ? runResult.result 
                                                        : JSON.stringify(runResult.result, null, 2)}
                                               </pre>
                                          )}
                                    </div>
                                )}
                            </div>
                         )}

                         {/* Error State */}
                         {runError && !isRunning && (
                             <div className="p-4 bg-red-950/20 border border-red-900/50 rounded-lg text-red-400 text-xs font-mono">
                                <p className="font-bold mb-1">[ERROR] Execution Failed</p>
                                <p>{runError}</p>
                             </div>
                         )}
                    </div>
                </div>
             </div>
        </div>

      </div>
    </div>
  );
};

export default RunAgentPage;
